import { useMemo, useState } from "react";
import { Copy, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { copyText } from "@/lib/gente-gestao/format";
import {
  csvConvitesUrl,
  downloadGgCsv,
  gerarConvitesApi,
  marcarEnviadoApi,
  type GgColaboradorDto,
  type GgConviteDto,
  type GgPesquisaDetalheDto,
} from "@/services/genteGestao";

function interpolar(template: string, vars: { nome: string; link: string; empresa: string; prazo: string }): string {
  return template
    .replace(/\{nome\}/g, vars.nome)
    .replace(/\{link\}/g, vars.link)
    .replace(/\{empresa\}/g, vars.empresa)
    .replace(/\{prazo\}/g, vars.prazo);
}

function prazoLabel(dataFim: string | null): string {
  if (!dataFim) return "sem prazo definido";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dataFim);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : dataFim;
}

export function AbaParticipantes({
  pesquisa,
  convites,
  colaboradoresAtivos,
  onChanged,
}: {
  pesquisa: GgPesquisaDetalheDto;
  convites: GgConviteDto[];
  colaboradoresAtivos: GgColaboradorDto[];
  onChanged: () => void;
}) {
  const [setor, setSetor] = useState("");
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [mensagem, setMensagem] = useState(pesquisa.mensagemConvite ?? "");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const setores = useMemo(() => {
    const s = new Set<string>();
    for (const c of colaboradoresAtivos) if (c.setor) s.add(c.setor);
    return [...s].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [colaboradoresAtivos]);

  function flash(text: string) {
    setFeedback(text);
    window.setTimeout(() => setFeedback(null), 2200);
  }

  async function gerar(opts: { todosAtivos?: boolean; setor?: string | null; colaboradorIds?: string[] }) {
    setBusy(true);
    try {
      const n = await gerarConvitesApi(pesquisa.id, opts);
      flash(n === 0 ? "Nenhum convite novo (já existiam)." : `${n} convite(s) gerado(s).`);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  function mensagemDe(c: GgConviteDto): string {
    return interpolar(mensagem || "", {
      nome: c.nome,
      link: c.link,
      empresa: pesquisa.nomeEmpresa,
      prazo: prazoLabel(pesquisa.dataFim),
    });
  }

  async function copiar(text: string, ok = "Copiado!") {
    const done = await copyText(text);
    flash(done ? ok : "Não foi possível copiar.");
  }

  const pendentes = convites.filter((c) => !c.respondidoEm);

  return (
    <div className="flex flex-col gap-4">
      {feedback && (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{feedback}</p>
      )}

      <div className="flex flex-wrap gap-2">
        {pesquisa.status !== "ATIVA" && (
          <p className="w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            O link público e os convites só funcionam depois de clicar em <strong>Ativar pesquisa</strong>.
          </p>
        )}
        <Button size="sm" disabled={busy} onClick={() => void gerar({ todosAtivos: true })}>
          Convidar todos os ativos
        </Button>
        <div className="flex items-center gap-2">
          <Select
            value={setor}
            onChange={(e) => setSetor(e.target.value)}
            options={setores.map((s) => ({ value: s, label: s }))}
            placeholder="Setor"
          />
          <Button size="sm" variant="secondary" disabled={!setor || busy} onClick={() => void gerar({ setor })}>
            Convidar setor
          </Button>
        </div>
        <Button
          size="sm"
          variant="secondary"
          disabled={selecionados.length === 0 || busy}
          onClick={() => void gerar({ colaboradorIds: selecionados })}
        >
          Convidar selecionados ({selecionados.length})
        </Button>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mensagem do WhatsApp</label>
        <textarea
          className="mt-1.5 min-h-[80px] w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
          value={mensagem}
          onChange={(e) => setMensagem(e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-400">Variáveis: {"{nome}"} {"{link}"} {"{empresa}"} {"{prazo}"}</p>
      </div>

      {pesquisa.permiteLinkGeral && pesquisa.linkGeral && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
          <p className="text-xs font-semibold uppercase text-slate-500">Link geral</p>
          <p className="mt-1 break-all text-sm text-slate-700">{pesquisa.linkGeral}</p>
          <p className="mt-2 text-xs text-slate-500">
            Qualquer pessoa com este link responde de forma anônima. Não pedimos nome e a gestão não vê quem respondeu.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button size="sm" variant="secondary" leftIcon={<Copy className="h-4 w-4" />} onClick={() => void copiar(pesquisa.linkGeral ?? "")}>
              Copiar link geral
            </Button>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(pesquisa.linkGeral)}`}
              width={160}
              height={160}
              alt="QR Code do link geral da pesquisa"
              className="rounded-lg border border-slate-200 bg-white"
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void copiar(convites.map((c) => `${c.nome} – ${c.link}`).join("\n"), "Lista copiada.")}
        >
          Copiar lista de links
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void downloadGgCsv(csvConvitesUrl(pesquisa.id), `convites-${pesquisa.slug}.csv`)}
        >
          Exportar CSV
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void copiar(pendentes.map((c) => `${c.nome} – ${c.link}`).join("\n"), "Pendentes copiados.")}
        >
          Copiar lista de pendentes
        </Button>
      </div>

      <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
        {convites.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-slate-500">Nenhum convite ainda.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Nome</th>
                <th className="px-3 py-2">Usuário</th>
                <th className="px-3 py-2">Setor</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {convites.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium">{c.nome}</td>
                  <td className="px-3 py-2">{c.usuario}</td>
                  <td className="px-3 py-2">{c.setor ?? "—"}</td>
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                      {c.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => void copiar(c.link)}>
                        Copiar link
                      </Button>
                      {c.telefone && (
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={<MessageCircle className="h-4 w-4" />}
                          onClick={() => {
                            const url = `https://wa.me/${c.telefone}?text=${encodeURIComponent(mensagemDe(c))}`;
                            window.open(url, "_blank", "noopener,noreferrer");
                            void marcarEnviadoApi(pesquisa.id, c.id).then(onChanged);
                          }}
                        >
                          WhatsApp
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void marcarEnviadoApi(pesquisa.id, c.id).then(onChanged)}
                      >
                        Marcar enviado
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {colaboradoresAtivos.filter((c) => !convites.some((x) => x.colaboradorId === c.id)).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="mb-2 text-xs font-semibold uppercase text-slate-500">Ativos ainda sem convite</p>
          <ul className="max-h-40 overflow-auto text-sm">
            {colaboradoresAtivos
              .filter((c) => !convites.some((x) => x.colaboradorId === c.id))
              .map((c) => (
                <li key={c.id} className="flex items-center gap-2 py-1">
                  <input
                    type="checkbox"
                    checked={selecionados.includes(c.id)}
                    onChange={(e) => {
                      setSelecionados((prev) =>
                        e.target.checked ? [...prev, c.id] : prev.filter((id) => id !== c.id),
                      );
                    }}
                  />
                  <span>
                    {c.nome} <span className="text-slate-400">({c.usuario})</span>
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
